import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * SIDEBAR NAVIGATION
 *
 * The row a sidebar is made of: an icon, a label, and sometimes something on
 * the right. The app had two of these — the main sidebar and the settings
 * sidebar — built independently and agreeing on almost everything, which is
 * the shape of a component that should have existed.
 *
 * Where they disagreed is more interesting than where they matched. The main
 * sidebar tinted an active item's icon with `text-primary`; settings did not.
 * Neither is wrong on its own; having both is. The tint stays, because an
 * active row is worth seeing at a glance and settings simply never got it.
 */

/**
 * The icon's hover and press animation.
 *
 * Both sidebars drove this from React state — two `useState`s and four mouse
 * handlers each, roughly fifteen lines per sidebar to make an icon wiggle.
 * `group-hover` and `group-active` do the same thing in CSS, and do it for
 * keyboard focus and touch as well, which the mouse handlers never did.
 */
export const navItemIconRecipe = cva(
  [
    uiMarker,
    'size-icon-sm shrink-0 transition-all duration-300 ease-out',
    'group-hover:scale-110 group-hover:rotate-3',
    'group-active:scale-75 group-active:-rotate-6',
  ],
  {
    variants: {
      active: {
        true: 'text-primary',
        false: '',
      },
    },
    defaultVariants: { active: false },
  }
);

/*
 * The surface a sidebar row sits on, shared by the expanded rows and the
 * collapsed icon rail.
 *
 * Pulled out because the rail hand-rolled `variant={active ? 'secondary' :
 * 'ghost'}` — the same borrowed Button variant NavItem used to — and so kept
 * its blue after the expanded rows moved to a card. The two shapes differ (a
 * full-width row with a label, an icon-only square) but the surface is one
 * decision, and this is where it lives.
 *
 * The `data-[force=hover]` twins are not optional: that attribute is how the
 * gallery shows a hover state without a mouse, and without them the catalogue
 * would keep showing Button's blue accent while the product shows white.
 */
const NAV_SURFACE = {
  /**
   * The current page, as a white card lifted off the sidebar.
   *
   * This used to borrow Button's `secondary`, which is `210 40% 96.1%` — a
   * *cool* near-white. Both sidebars sit on `--canvas`, `42 24% 95.6%` — a
   * *warm* one. At those lightnesses only the hue separated them, so it read as
   * a blue tint rather than a raised surface. `card` is the surface every other
   * raised thing uses, and the elevation does the separating instead.
   */
  true: [
    'bg-card text-foreground shadow-e1',
    'hover:bg-card hover:text-foreground',
    'data-[force=hover]:bg-card data-[force=hover]:text-foreground',
  ].join(' '),
  false: [
    'hover:bg-card/60 hover:text-foreground',
    'data-[force=hover]:bg-card/60 data-[force=hover]:text-foreground',
  ].join(' '),
} as const;

/** For a sidebar control that is not a NavItem — the collapsed rail's icons. */
export const navSurfaceRecipe = cva([uiMarker], {
  variants: { active: NAV_SURFACE },
  defaultVariants: { active: false },
});

export const navItemRecipe = cva(
  [uiMarker, 'group w-full justify-start gap-2'],
  {
    variants: {
      active: NAV_SURFACE,
      /** Dimmed without being disabled — a hidden item in the sidebar's edit mode. */
      muted: {
        true: 'opacity-50 text-muted-foreground',
        false: '',
      },
    },
    defaultVariants: { active: false, muted: false },
  }
);

export type NavItemRecipeProps = VariantProps<typeof navItemRecipe>;
